import { colors, ContributionInterval, CreateContributionIntentInput, CreateContributionIntentResult } from '@hazadapt-git/public-core-base'
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Grid,
    Link,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material'
import React, { FC } from 'react'
import {
    Elements,
    PaymentElement,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { ContributeSupportLinksBlock } from '../molecules'
import { getEnvVars } from '../../lib/config'

interface ContributeTabProps {
    onContributePress(
        input: CreateContributionIntentInput
    ): Promise<CreateContributionIntentResult | string>
}

const PRESET_OPTIONS = [
    {
        amount: 5,
        quip: '☕ Get the devs a coffee',
        isSuggested: false,
    },
    {
        amount: 10,
        quip: '⭐ Share HazAdapt in one more school',
        isSuggested: true,
    },
    {
        amount: 25,
        quip: '🌱 Spark preparedness in a community',
        isSuggested: false,
    },
    {
        amount: 100,
        quip: '🚀 Power HazAdapt Safety App for a week',
        isSuggested: false,
    },
]

const SHARE_AND_REVIEW_TEXT =
    'We understand that times are tough and not everyone can contribute financially. If you enjoy HazAdapt and still want to help, consider sharing or leaving a review.'
const CONFIRMATION_SUPPORT_TEXT =
    'Feeling extra generous? Leave a review or share the app.'

interface ContributePaymentFormProps {
    amount: number
    mode: 'payment' | 'subscription'
}

const ContributePaymentForm: FC<ContributePaymentFormProps> = ({
    amount,
    mode,
}: ContributePaymentFormProps) => {
    const stripe = useStripe()
    const elements = useElements()

    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false)
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null)

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setErrorMessage(null)

        if (!stripe || !elements) {
            setErrorMessage(
                'Payment is not ready yet. Please wait a moment and try again.'
            )
            return
        }

        if (!amount || amount <= 0) {
            setErrorMessage('Please select a valid contribution amount.')
            return
        }

        setIsSubmitting(true)

        const { error } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: `${window.location.origin}/settings/contribute`,
            },
        })

        if (error) {
            setErrorMessage(
                error.message || 'Unable to complete your contribution.'
            )
        }

        setIsSubmitting(false)
    }

    return (
        <Stack spacing={3} component="form" onSubmit={onSubmit}>
            <Typography variant="h6">
                Contribution: Total: ${amount.toFixed(2)}
                {mode === 'subscription' ? ' monthly' : ''}
            </Typography>

            <PaymentElement />

            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

            <Button
                type="submit"
                variant="contained"
                disabled={!stripe || !elements || isSubmitting}
                startIcon={
                    isSubmitting ? <CircularProgress size={18} /> : undefined
                }
            >
                {isSubmitting ? 'Processing...' : 'Chip In'}
            </Button>
        </Stack>
    )
}

export const ContributeTab: FC<ContributeTabProps> = ({
    onContributePress,
}: ContributeTabProps) => {
    const { REACT_APP_STRIPE_PUBLIC_KEY } = getEnvVars()
    const publishableKey = REACT_APP_STRIPE_PUBLIC_KEY
    const [paymentIntentClientSecret] = React.useState<string | null>(() => {
        const params = new URLSearchParams(window.location.search)
        return params.get('payment_intent_client_secret')
    })
    const [selectedPresetAmount, setSelectedPresetAmount] = React.useState<
        number | null
    >(PRESET_OPTIONS[1].amount)
    const [customAmountInput, setCustomAmountInput] = React.useState<string>('')
    const [isCreatingIntent, setIsCreatingIntent] = React.useState<boolean>(false)
    const [clientSecret, setClientSecret] = React.useState<string>('')
    const [paymentMode, setPaymentMode] = React.useState<
        'payment' | 'subscription'
    >('payment')
    const [intentError, setIntentError] = React.useState<string | null>(null)
    const [frequency, setFrequency] = React.useState<'one_time' | ContributionInterval>(
        'one_time'
    )
    const [confirmationState, setConfirmationState] = React.useState<{
        isLoading: boolean
        isVisible: boolean
        title: string
        body: string
        detailLines: string[]
    }>({
        isLoading: false,
        isVisible: false,
        title: '',
        body: '',
        detailLines: [],
    })
    const latestIntentRequestId = React.useRef<number>(0)

    const selectedAmount = React.useMemo(() => {
        const parsedCustomAmount = Number(customAmountInput)
        if (!Number.isNaN(parsedCustomAmount) && parsedCustomAmount > 0) {
            return parsedCustomAmount
        }

        return selectedPresetAmount ?? 0
    }, [customAmountInput, selectedPresetAmount])

    const onCreatePaymentIntent = React.useCallback(
        async (
            requestId: number,
            amount: number,
            currentFrequency: 'one_time' | ContributionInterval
        ) => {
            setIsCreatingIntent(true)
            try {
                const isRecurring = currentFrequency !== 'one_time'
                const intentResponse = await Promise.resolve(
                    onContributePress({
                        amount: Math.round(amount * 100),
                        isRecurring,
                        interval: isRecurring ? currentFrequency : undefined,
                        platform: 'web',
                        currency: 'usd',
                    })
                )

                if (requestId !== latestIntentRequestId.current) return

                const maybeClientSecret =
                    typeof intentResponse === 'string'
                        ? intentResponse
                        : intentResponse?.clientSecret
                const mode =
                    typeof intentResponse === 'string'
                        ? isRecurring
                            ? 'subscription'
                            : 'payment'
                        : intentResponse?.mode ??
                          (isRecurring ? 'subscription' : 'payment')

                if (
                    typeof maybeClientSecret !== 'string' ||
                    !maybeClientSecret.includes('_secret_')
                ) {
                    setIntentError(
                        'Unable to start payment. Make sure the API returns a valid Stripe clientSecret.'
                    )
                    return
                }

                setClientSecret(maybeClientSecret)
                setPaymentMode(mode)
            } catch (err) {
                if (requestId !== latestIntentRequestId.current) return
                console.error(err)
                setIntentError('Unable to start contribution. Please try again.')
            } finally {
                if (requestId !== latestIntentRequestId.current) return
                setIsCreatingIntent(false)
            }
        },
        [onContributePress]
    )

    React.useEffect(() => {
        // Prevent stale requests from updating state after unmount.
        return () => {
            latestIntentRequestId.current += 1
        }
    }, [])

    const onPreparePaymentPress = React.useCallback(() => {
        if (paymentIntentClientSecret) return
        if (!publishableKey) {
            setIntentError(
                'Stripe is not configured yet. Please try again later.'
            )
            return
        }
        if (!selectedAmount || selectedAmount < 1) {
            setIntentError('Please select an amount of at least $1.00.')
            return
        }

        setIntentError(null)
        setClientSecret('')
        const requestId = ++latestIntentRequestId.current
        void onCreatePaymentIntent(requestId, selectedAmount, frequency)
    }, [
        frequency,
        onCreatePaymentIntent,
        paymentIntentClientSecret,
        publishableKey,
        selectedAmount,
    ])

    React.useEffect(() => {
        // Stripe may append sensitive query params to the return URL.
        // Remove them from the address bar/history as soon as possible.
        if (!paymentIntentClientSecret) return
        const sanitized = `${window.location.origin}${window.location.pathname}`
        window.history.replaceState({}, document.title, sanitized)
    }, [paymentIntentClientSecret])

    React.useEffect(() => {
        if (!paymentIntentClientSecret || !publishableKey) return

        let active = true
        setConfirmationState((prev) => ({
            ...prev,
            isLoading: true,
            isVisible: false,
        }))

        void loadStripe(publishableKey)
            .then(async (stripe) => {
                if (!stripe) throw new Error('Stripe failed to initialize.')
                return stripe.retrievePaymentIntent(paymentIntentClientSecret)
            })
            .then((result) => {
                if (!active) return
                if (result.error || !result.paymentIntent) {
                    throw new Error(
                        result.error?.message ||
                            'We could not verify your contribution yet.'
                    )
                }

                const paymentIntent = result.paymentIntent
                const amount = (paymentIntent.amount ?? 0) / 100
                const currency = (paymentIntent.currency || 'usd').toUpperCase()
                const receiptEmail = paymentIntent.receipt_email
                const createdAt = paymentIntent.created
                    ? new Date(paymentIntent.created * 1000).toLocaleString()
                    : null

                const detailLines = [
                    `Contribution amount: ${currency} ${amount.toFixed(2)}`,
                    `Payment reference: ${paymentIntent.id}`,
                    ...(receiptEmail
                        ? [`Receipt email: ${receiptEmail}`]
                        : ['Receipt email: Not provided']),
                    ...(createdAt ? [`Confirmation time: ${createdAt}`] : []),
                ]

                if (paymentIntent.status === 'succeeded') {
                    setConfirmationState({
                        isLoading: false,
                        isVisible: true,
                        title: 'Thank you for your contribution!',
                        body: receiptEmail
                            ? 'Your contribution was successful. A confirmation email has been sent with your receipt details, contribution amount, and transaction reference.'
                            : 'Your contribution was successful. We could not confirm an email address for this receipt, but your transaction details are below.',
                        detailLines,
                    })
                    return
                }

                if (
                    paymentIntent.status === 'processing' ||
                    paymentIntent.status === 'requires_capture'
                ) {
                    setConfirmationState({
                        isLoading: false,
                        isVisible: true,
                        title: 'Your contribution is processing',
                        body: receiptEmail
                            ? 'We are finalizing your contribution. You will receive a confirmation email once payment is complete, including your contribution and receipt details.'
                            : 'We are finalizing your contribution. We could not confirm an email address for this receipt, but your transaction details are below.',
                        detailLines,
                    })
                    return
                }

                throw new Error(
                    'Your payment was not completed. Please try again or use a different payment method.'
                )
            })
            .catch((err: unknown) => {
                if (!active) return
                const message =
                    err instanceof Error
                        ? err.message
                        : 'Unable to verify contribution status.'
                setIntentError(message)
                setConfirmationState((prev) => ({
                    ...prev,
                    isLoading: false,
                    isVisible: false,
                }))
            })

        return () => {
            active = false
        }
    }, [paymentIntentClientSecret, publishableKey])

    const stripePromise = React.useMemo(() => {
        if (!publishableKey) return null
        return loadStripe(publishableKey)
    }, [publishableKey])

    if (confirmationState.isLoading) {
        return (
            <Stack spacing={2} alignItems="center" py={3}>
                <CircularProgress size={28} />
                <Typography sx={{ color: colors.grays.THUNDERCLOUD }}>
                    Verifying your contribution...
                </Typography>
            </Stack>
        )
    }

    if (confirmationState.isVisible) {
        return (
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Typography variant="h4" textAlign="center">
                        {confirmationState.title}
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Typography
                        variant="body1"
                        textAlign="center"
                        sx={{ color: colors.grays.THUNDERCLOUD }}
                    >
                        {confirmationState.body}
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Box
                        sx={{
                            border: '1px solid',
                            borderColor: colors.grays.NIMBUS,
                            borderRadius: 1,
                            p: 2,
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight={600}>
                            Confirmation email details
                        </Typography>
                        {confirmationState.detailLines.map((line) => (
                            <Typography
                                key={line}
                                variant="body2"
                                sx={{ color: colors.grays.THUNDERCLOUD }}
                            >
                                {line}
                            </Typography>
                        ))}
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <ContributeSupportLinksBlock text={CONFIRMATION_SUPPORT_TEXT} />
                </Grid>
            </Grid>
        )
    }

    return (
        <Grid
            container
            spacing={3}
            height="100%"
            sx={{ minWidth: 0, maxWidth: '100%' }}
        >
            <Grid item xs={12}>
                <Typography textAlign="center">
                    Choose an amount and chip in securely through Stripe.
                </Typography>
            </Grid>

            <Grid item xs={12}>
                <Stack spacing={3}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: 'minmax(0, 1fr)',
                                sm: 'repeat(2, minmax(0, 1fr))',
                                lg: 'repeat(4, minmax(0, 1fr))',
                            },
                            gap: 1,
                            width: '100%',
                            minWidth: 0,
                        }}
                    >
                            {PRESET_OPTIONS.map((option) => (
                                <ToggleButton
                                    value={option.amount}
                                    key={`amount-${option.amount}`}
                                    selected={
                                        selectedPresetAmount === option.amount
                                    }
                                    onChange={() => {
                                        setSelectedPresetAmount(option.amount)
                                        setCustomAmountInput('')
                                        setClientSecret('')
                                        setIntentError(null)
                                    }}
                                    sx={{
                                        width: '100%',
                                        minWidth: 0,
                                        whiteSpace: 'normal',
                                    }}
                                >
                                    <Stack spacing={0.25} alignItems="center">
                                        <Typography fontWeight={700}>
                                            ${option.amount}{' '}
                                            {option.isSuggested ? '(Suggested)' : ''}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: colors.grays.THUNDERCLOUD,
                                                textAlign: 'center',
                                                overflowWrap: 'anywhere',
                                            }}
                                        >
                                            {option.quip}
                                        </Typography>
                                    </Stack>
                                </ToggleButton>
                            ))}
                    </Box>

                    <TextField
                        label="Custom amount (USD)"
                        type="number"
                        inputProps={{ min: 1, step: 1 }}
                        value={customAmountInput}
                        onChange={(event) => {
                            const nextValue = event.target.value
                            setCustomAmountInput(nextValue)
                            if (nextValue.trim().length > 0) {
                                setSelectedPresetAmount(null)
                            }
                            setClientSecret('')
                            setIntentError(null)
                        }}
                        helperText="Entering a custom amount clears preset selection."
                        fullWidth
                    />

                    <Box>
                        <Typography mb={1}>Chip in frequency</Typography>
                        <ToggleButtonGroup
                            color="primary"
                            exclusive
                            value={frequency}
                            onChange={(_, newFrequency) => {
                                if (newFrequency) {
                                    setFrequency(newFrequency)
                                    setClientSecret('')
                                    setIntentError(null)
                                }
                            }}
                            fullWidth
                        >
                            <ToggleButton value="one_time">One-time</ToggleButton>
                            <ToggleButton value="month">Monthly</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {!clientSecret && (
                        <Typography variant="h6">
                            Total: ${selectedAmount.toFixed(2)}
                            {frequency === 'month' ? ' monthly' : ''}
                        </Typography>
                    )}

                    {!clientSecret && (
                        <Button
                            variant="contained"
                            onClick={onPreparePaymentPress}
                            disabled={isCreatingIntent}
                            startIcon={
                                isCreatingIntent ? (
                                    <CircularProgress size={18} />
                                ) : undefined
                            }
                        >
                            {isCreatingIntent
                                ? 'Preparing payment...'
                                : 'Continue to payment'}
                        </Button>
                    )}

                    {intentError && <Alert severity="error">{intentError}</Alert>}
                </Stack>
            </Grid>

            <Grid item xs={12}>
                {!publishableKey || !stripePromise ? (
                    <Alert severity="info">
                        Stripe is not configured yet. Set
                        REACT_APP_STRIPE_PUBLIC_KEY to enable contributions.
                    </Alert>
                ) : !clientSecret ? (
                    <Alert severity="info">
                        Select your contribution and press "Continue to payment."
                    </Alert>
                ) : (
                    <Elements
                        key={clientSecret}
                        stripe={stripePromise}
                        options={{ clientSecret }}
                    >
                        <ContributePaymentForm
                            amount={selectedAmount}
                            mode={paymentMode}
                        />
                    </Elements>
                )}
            </Grid>
            <Grid item xs={12}>
                <ContributeSupportLinksBlock
                    text={SHARE_AND_REVIEW_TEXT}
                />
            </Grid>
            <Grid item xs={12}>
                <Typography
                    variant="body2"
                    sx={{ color: colors.grays.THUNDERCLOUD }}
                >
                    HazAdapt is a small, US-based business building tools to help
                    people prepare for and respond to hazards. Your contribution
                    helps us keep building, reach more people, and keep the app
                    running strong and ad-free. Your gift is not tax-deductible and
                    does not come with goods or services in return - but every bit
                    helps us do this work, and we are genuinely grateful for it. If
                    you would like a Community Sponsorship with added benefits
                    instead,{' '}
                    <Link
                        href="https://www.hazadapt.com/community-sponsors"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        see here
                    </Link>
                </Typography>
            </Grid>
        </Grid>
    )
}
